import {
  Hps_Liquid_Type_Enum,
  Well_Gas_Type_Options_Enum,
  Well_Test_Liquid_Input_Options_Enum
} from "gql/api";

const isCondensateLiquidType = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): boolean => hpsLiquidType === Hps_Liquid_Type_Enum.Condensate;

export const isHpsWaterLiquidType = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): boolean =>
  hpsLiquidType === Hps_Liquid_Type_Enum.Water ||
  hpsLiquidType === Hps_Liquid_Type_Enum.Amine;

export const isHpsOilLiquidType = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): boolean => hpsLiquidType === Hps_Liquid_Type_Enum.Oil;

export const isHpsCondensateLiquidType = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): boolean => hpsLiquidType === Hps_Liquid_Type_Enum.Condensate;

export const getHpsOilApiLabel = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): string =>
  isHpsCondensateLiquidType(hpsLiquidType) ? "Condensate API" : "Oil API";

export const shouldHideHpsOilRateRow = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): boolean => isHpsCondensateLiquidType(hpsLiquidType);

export const shouldHideHpsWaterCutRow = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): boolean =>
  hpsLiquidType === Hps_Liquid_Type_Enum.Amine ||
  hpsLiquidType === Hps_Liquid_Type_Enum.Condensate;

export const isHpsLiquidTypeWithZeroedFlowRates = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): boolean =>
  hpsLiquidType === Hps_Liquid_Type_Enum.Amine ||
  hpsLiquidType === Hps_Liquid_Type_Enum.Water;

export const isHpsLiquidTypeWithZeroedWaterRate = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): boolean => hpsLiquidType === Hps_Liquid_Type_Enum.Oil;

export const isHpsLiquidTypeWithZeroedWaterCut = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): boolean => hpsLiquidType === Hps_Liquid_Type_Enum.Condensate;

export const getHpsWaterCutResetState = (
  previousHpsLiquidType?: Hps_Liquid_Type_Enum | string | null,
  nextHpsLiquidType?: Hps_Liquid_Type_Enum | string | null,
  previousWaterCutPercentage = 0
): {
  waterCutPercentage: number;
} => {
  if (isHpsLiquidTypeWithZeroedWaterCut(nextHpsLiquidType)) {
    return {
      waterCutPercentage: 0
    };
  }

  if (isHpsLiquidTypeWithZeroedWaterCut(previousHpsLiquidType)) {
    return {
      waterCutPercentage: previousWaterCutPercentage
    };
  }

  return {
    waterCutPercentage: previousWaterCutPercentage
  };
};

export const isHpsLiquidTypeWithWaterCutInput = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null,
  isHps = false
): boolean => {
  if (!isHps) {
    return true;
  }

  return (
    hpsLiquidType !== Hps_Liquid_Type_Enum.Amine &&
    hpsLiquidType !== Hps_Liquid_Type_Enum.Water &&
    hpsLiquidType !== Hps_Liquid_Type_Enum.Oil &&
    hpsLiquidType !== Hps_Liquid_Type_Enum.Condensate
  );
};

export const getForcedGasTypeForHpsLiquidType = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): Well_Gas_Type_Options_Enum =>
  isCondensateLiquidType(hpsLiquidType)
    ? Well_Gas_Type_Options_Enum.Wet
    : Well_Gas_Type_Options_Enum.Dry;

export const getHpsFlowRateResetState = (
  previousHpsLiquidType?: Hps_Liquid_Type_Enum | string | null,
  nextHpsLiquidType?: Hps_Liquid_Type_Enum | string | null,
  previousOilFlowRateStbd = 0,
  previousGasFlowRateMscf = 0
): {
  oilFlowRateStbd: number;
  gasFlowRateMscf: number;
} => {
  if (isHpsLiquidTypeWithZeroedFlowRates(nextHpsLiquidType)) {
    return {
      oilFlowRateStbd: 0,
      gasFlowRateMscf: 0
    };
  }

  if (isHpsLiquidTypeWithZeroedFlowRates(previousHpsLiquidType)) {
    return {
      oilFlowRateStbd: previousOilFlowRateStbd,
      gasFlowRateMscf: previousGasFlowRateMscf
    };
  }

  return {
    oilFlowRateStbd: previousOilFlowRateStbd,
    gasFlowRateMscf: previousGasFlowRateMscf
  };
};

export const getHpsWaterRateResetState = (
  previousHpsLiquidType?: Hps_Liquid_Type_Enum | string | null,
  nextHpsLiquidType?: Hps_Liquid_Type_Enum | string | null,
  previousWaterFlowRateStbd = 0
): {
  waterFlowRateStbd: number;
} => {
  if (isHpsLiquidTypeWithZeroedWaterRate(nextHpsLiquidType)) {
    return {
      waterFlowRateStbd: 0
    };
  }

  if (isHpsLiquidTypeWithZeroedWaterRate(previousHpsLiquidType)) {
    return {
      waterFlowRateStbd: previousWaterFlowRateStbd
    };
  }

  return {
    waterFlowRateStbd: previousWaterFlowRateStbd
  };
};

export const getHpsSingleLiquidRateLabel = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): string => {
  switch (hpsLiquidType) {
    case Hps_Liquid_Type_Enum.Water:
    case Hps_Liquid_Type_Enum.Multiphase:
    case Hps_Liquid_Type_Enum.Condensate:
    case Hps_Liquid_Type_Enum.Oil:
      return "Water Rate";
    case Hps_Liquid_Type_Enum.Amine:
      return "Amine Rate";
    default:
      return "Rate";
  }
};

export const getHpsWaterGravityLabel = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): string =>
  hpsLiquidType === Hps_Liquid_Type_Enum.Amine
    ? "Amine Gravity"
    : "Water Gravity";

export const getHpsOilGravityLabel = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): string =>
  hpsLiquidType === Hps_Liquid_Type_Enum.Condensate
    ? "Condensate Gravity"
    : "Oil Gravity";

export const getHpsLiquidGravityLabel = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): string => getHpsWaterGravityLabel(hpsLiquidType);

export const getHpsTotalLiquidRateLabel = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): string =>
  isCondensateLiquidType(hpsLiquidType)
    ? "Condensate Rate"
    : "Total Liquid Rate";

export const getForcedLiquidInputForHpsLiquidType = (
  hpsLiquidType?: Hps_Liquid_Type_Enum | string | null
): Well_Test_Liquid_Input_Options_Enum | null => {
  switch (hpsLiquidType) {
    case Hps_Liquid_Type_Enum.Amine:
    case Hps_Liquid_Type_Enum.Water:
      return Well_Test_Liquid_Input_Options_Enum.OilRateWaterRate;
    case Hps_Liquid_Type_Enum.Oil:
      return Well_Test_Liquid_Input_Options_Enum.OilRateWaterCut;
    case Hps_Liquid_Type_Enum.Condensate:
      return Well_Test_Liquid_Input_Options_Enum.TotalRateWaterCut;
    case Hps_Liquid_Type_Enum.Multiphase:
    default:
      return null;
  }
};
